<template>
  <b-tab 
    title="Transactions View" 
    title-item-class="payment-portal__tab"
    active
    lazy
  >
  <!-- Loader -->
    <template v-if="isBusy">
      <div class="d-flex justify-content-center">
        <b-spinner 
          class="align-middle"
        />
      </div>
    </template>

    <app-collapse
      v-else
      v-for="(transactionsPerApplication, key) in transactionsTableItems"
      :key="key"
      class="mb-3" 
    >
      <app-collapse-item
        title
        :is-visible="false"
      >
        <template #header>
          <div class="w-100">
            <h5
              class="d-flex align-items-center mb-0"
            >
              {{transactionsPerApplication[0].application_user_name}} - {{transactionsPerApplication[0].program_name}}
              <b-badge
                variant="light"
                class="text-muted ml-3 mr-2 p-2"
              >
                <span class="font-weight-normal">Remaining balance:</span> <b class="font-weight-bold">{{transactionsPerApplication[transactionsPerApplication.length - 1].remaining_total | fixAmount}}</b>
              </b-badge>
            </h5>

            <!-- PC reminder notification -->
            <div
              v-if="needsPcReminder(key)"
              class="pc-reminder"
              @click.stop
            >
              <span class="pc-reminder__icon">!</span>
              <span class="pc-reminder__text">Payment contract not selected.</span>
              <router-link
                :to="getPcStepRoute(key)"
                class="pc-reminder__link"
              >
                Go to payment option step →
              </router-link>
            </div>
          </div>
        </template>

        <div class="overflow-auto">
          <b-table
            ref="refTransactionsTable"
            :items="transactionsPerApplication"
            :fields="tableColumns"
            :busy="isBusy"
            thead-class="table-header"
            :tbody-tr-class="rowClass"
            primary-key="id"
            show-empty
            empty-text="No matching records found"
            class="position-relative w-100"
          >

            <!-- Loader -->
            <template #table-busy>
              <div class="d-flex justify-content-center">
                <b-spinner 
                  class="align-middle"
                />
              </div>
            </template>

            <!-- Column: Id -->
            <template #cell(id)="data">
              <span :id="data.item.id">{{ data.item.id | shortId }}</span>
              <b-tooltip
                triggers="hover"
                :target="data.item.id"
                :delay="{ show: 100, hide: 50 }"
              >
                {{ data.item.id }}
              </b-tooltip>
            </template>

            <!-- Column: Date -->
            <template #cell(payment_date)="data">
              <span :id="data.item.payment_date + data.item.id">{{ data.item.payment_date | shortDate }}</span>
              <b-tooltip
                triggers="hover"
                :target="data.item.payment_date + data.item.id"
                :delay="{ show: 100, hide: 50 }"
              >
                {{ data.item.payment_date | longDate }}
              </b-tooltip>
            </template>

            <!-- Column: model_type -->
            <template #cell(model_type)="data">
              {{ data.item.model_type == 'Charge' ? 'Invoice' : data.item.model_type }}
            </template>

            <!-- Column: amount -->
            <template #cell(amount)="data">
              {{ data.value | fixAmount }}
            </template>


            <!-- Column: type -->
            <template #cell(type)="data">
              {{ data.item.type == 'Charge' ? 'Invoice' : data.item.type }}
            </template>

            <!-- Column: status -->
            <template #cell(status)="data">
              <b-badge
                pill
                :variant="`light-${resolveStatusVariant(data.value)}`"
              >
                {{ data.value }}
              </b-badge>
            </template>

          </b-table>
        </div>
      </app-collapse-item>
    </app-collapse>
  </b-tab>
</template>

<script>
import { mapGetters } from 'vuex'
import store from "@/store"

import AppCollapse from '@core/components/app-collapse/AppCollapse.vue'
import AppCollapseItem from '@core/components/app-collapse/AppCollapseItem.vue'

import { 
  BTab, BTable, BTooltip, BBadge, BSpinner,
} from 'bootstrap-vue'

import { filters } from '@core/mixins/filters'

export default {
  name: "TransactionsViewTab",
  components: {
    AppCollapse,
    AppCollapseItem,
    BTab,
    BTable,
    BTooltip,
    BBadge,
    BSpinner,
  },
  mixins: [ filters ],
  data() {
    return {
      tableColumns: [
        { key: 'id', label: 'Id' },
        { key: 'payment_date', label: 'Date' },
        { key: 'model_type', label: 'Type' },
        { key: 'amount', label: 'Amount' },
        { key: 'description', label: 'Description' },
        { key: 'type', label: 'Payment method' },
        { key: 'status', label: 'Status' },
      ],
    }
  },
  async created() {
    await store.dispatch('paymentPortal/fetchTransactionsList')
  },
  computed: {
    ...mapGetters({
      isBusy: 'paymentPortal/getIsTransactionsTableBusy',
      transactionsTableItems: 'paymentPortal/getTransactionsTableItems',
      transactionsApplicationMeta: 'paymentPortal/getTransactionsApplicationMeta',
    }),
  },
  methods: {
    /**
     * Show the PC reminder when:
     *  - application is accepted
     *  - has no PC selected yet (has_pc = false)
     *  - the registration form has at least one step with a payment contract (has_pc_step = true)
     */
    needsPcReminder(applicationId) {
      const meta = this.transactionsApplicationMeta[applicationId]
      if (!meta) return false
      return meta.is_accepted && !meta.has_pc && meta.has_pc_step
    },
    /**
     * Build the route to the PC step inside ApplicationSteps.vue.
     * Uses the program_id as route param (matching :id in /application/:id)
     * and passes app_id + tab_index as query params.
     */
    getPcStepRoute(applicationId) {
      const meta = this.transactionsApplicationMeta[applicationId]
      if (!meta) return {}
      return {
        name: 'application-create',
        params: { id: meta.program_id },
        query: {
          app_id: meta.application_id,
          user_id: meta.user_id,
          navigate_to_pc: 1,
        },
      }
    },
    rowClass(item, type) {
      if (!item || type !== 'row') return
      if (item.status.toLowerCase() === 'payment failed') return 'text-primary'
      if (item.status.toLowerCase() === 'payment failed (retried)') return 'text-primary'
      if (item.model_type === 'Charge') return 'text-danger'
      if (item.model_type === 'Payment') return 'text-success'
    },
    resolveStatusVariant(status) {
      if (status.toLowerCase() === 'completed') return 'success'
      if (status.toLowerCase() === 'applied') return 'success'
      if (status.toLowerCase() === 'payment failed') return 'primary'
      if (status.toLowerCase() === 'payment failed (retried)') return 'primary'
      if (status.toLowerCase() === 'pending') return 'warning'
      if (status.toLowerCase() === 'canceled') return 'pink'
      if (status.toLowerCase() === 'manual cancel') return 'warning'
      if (status.toLowerCase() === 'refunded') return 'purple'
      return 'primary'
    },
  },
}
</script>

<style scoped lang="scss">
.pc-reminder {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 10px;
  margin-right: 30px;
  padding: 10px 16px;
  border: 1px solid $primary;
  border-radius: 10px;
  background-color: rgba($secondary_bg, 0.15);
  font-size: 14px;
  color: $primary;

  &__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background-color: $primary;
    color: #fff;
    font-weight: 700;
    font-size: 13px;
    line-height: 1;
  }

  &__text {
    flex: 1;
    font-weight: 600;
    color: $primary;
  }

  &__link {
    flex-shrink: 0;
    font-weight: 700;
    color: $primaryButton;
    text-decoration: none;
    border-bottom: 1px solid $primaryButton;
    transition: opacity 0.2s ease;

    &:hover {
      opacity: 0.75;
      text-decoration: none;
    }
  }
}
</style>